import React, { useState } from 'react';
import { Divider, Flex, Spin, Switch, Typography } from '@duxton-web/ui';

import { normalizeRollout } from '../RolloutTab/diffUtils';
import StatusMessage from './StatusMessage';
import { useVariable } from '@/contexts/VariableContext';
import { RolloutDiff } from '@/lib/PlannerDTOs';
import { RolloutDiff as ChangelogRolloutDiff } from '@/pages/Changelog/ChangelogItem';
import { getModifiedRolloutTargetsMap } from '@/utils/changelog';

interface RolloutDiffV2Props {
  diffId: string;
  rolloutDiff: RolloutDiff | null;
}

const RolloutDifferenceV2: React.FC<RolloutDiffV2Props> = ({ rolloutDiff }) => {
  const variable = useVariable();
  const [includeUnchanged, setIncludeUnchanged] = useState(false);
  const [togglingShowAllRollout, setTogglingShowAllRollout] = useState(false);

  const renderShowAllRolloutsToggle = () => (
    <Flex
      gap={8}
      align="center"
      style={{
        padding: '6px 14px',
        borderRadius: 48,
      }}
    >
      <Switch
        checked={includeUnchanged}
        onChange={(checked) => {
          setIncludeUnchanged(checked);
          setTogglingShowAllRollout(true);
          setTimeout(() => setTogglingShowAllRollout(false), 1000);
        }}
        loading={togglingShowAllRollout}
        disabled={!rolloutDiff || togglingShowAllRollout}
      />
      <Typography.Body level={3} type="secondary" style={{ lineHeight: 0 }}>
        Show all rollouts
      </Typography.Body>
    </Flex>
  );

  if (!rolloutDiff) {
    return <Spin />;
  }

  const {
    newRollout,
    oldRollout,
    createdBy,
    state,
    updatedBy,
    createdAt,
    updatedAt,
    expireAt,
    description,
    requestLink,
  } = rolloutDiff;

  const modifiedRolloutTargets = getModifiedRolloutTargetsMap(
    normalizeRollout(newRollout),
    normalizeRollout(oldRollout),
    includeUnchanged,
  );

  return (
    <Flex gap={8} vertical style={{ padding: '8px 24px' }}>
      <StatusMessage
        state={state}
        updatedBy={updatedBy!}
        updatedAt={updatedAt!}
        createdAt={createdAt}
        createdBy={createdBy}
        expireAt={expireAt}
      />
      <Flex
        gap={8}
        vertical
        style={{
          background: 'white',
          padding: 16,
          borderRadius: 16,
          border: '1px solid #d9d9d9',
        }}
      >
        <Flex vertical gap={8}>
          {description && (
            <Flex gap={4} align="center">
              <Typography.Body type="secondary" level={3} strong>
                {description}
              </Typography.Body>
              <Typography.Metadata
                style={{
                  background: 'rgb(0 177 79 / 10%)',
                  borderRadius: 4,
                  color: '#00B14F',
                  fontWeight: 700,
                  padding: 4,
                }}
              >
                Rollout Changes
              </Typography.Metadata>
            </Flex>
          )}

          {requestLink && (
            <Typography.Metadata type="secondary">
              Jira Ticket:{' '}
              <a href={requestLink} target="_blank" rel="noreferrer noopenner">
                {requestLink}
              </a>
            </Typography.Metadata>
          )}
        </Flex>

        {(description || requestLink) && (
          <Divider style={{ margin: '4px 0px' }} />
        )}

        <Flex justify="space-between" align="center">
          <Typography.Body level={3} type="secondary">
            Changes Made
          </Typography.Body>
          {renderShowAllRolloutsToggle()}
        </Flex>
        <ChangelogRolloutDiff
          rolloutTargets={modifiedRolloutTargets}
          variable={variable}
        />
      </Flex>
    </Flex>
  );
};

export default RolloutDifferenceV2;
